import type { BlockBase, BlockDefinition, BlockSerialized } from '#layers/block-engine/types/block';
import { createEventHook } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, reactive } from 'vue';
import { useBlockAdapter } from '#layers/block-engine/composables/use-block-adapter';
import { getBlockConfigFromComponent } from '#layers/block-engine/utils/block/api/config';
import { getBuiltinBlocks } from '#layers/block-engine/utils/block/builtin-registry';
import { slotOutletBlockId } from '#layers/block-engine/utils/block/const';
import { mapBlockDataToSerialized } from '#layers/block-engine/utils/block/serialized-adapter';
import { useTranslationUtils } from '#layers/i18n/public';
import { defineWorkspaceEntityHydration, workspaceRuntime } from '#layers/runtime/public';

interface UseFilteredBlocksOptions {
	filter?: (block: BlockSerialized | BlockDefinition | BlockBase) => boolean;
}

const unspecifiedCategory = 'uncategorized';
const excludedCategories = new Set([
	'space',
	'page',
]);

export const blockDefinitionTranslatableKeys = ['name', 'description'] as const;

/** The block library of a workspace runtime. */
export const workspaceBlocksHydration = defineWorkspaceEntityHydration((store) => store.MonospaceBlock.query((q) => q.many({})), { empty: [] });

export const useBlockLibraryStore = defineStore('block-library', () => {
	const { translateObject } = useTranslationUtils();

	const isReady = computed(() => workspaceRuntime.activeRuntime.value !== null);
	const { data: workspaceBlockRows } = workspaceBlocksHydration.use();
	const serializedWorkspaceBlocks = computed(() => workspaceBlockRows.value.map((block) => mapBlockDataToSerialized(block)));

	const builtinBlocks = computed(() => Object.values(getBuiltinBlocks()).map((info) => {
		const config = getBlockConfigFromComponent(info.comp);

		return translateObject({
			...info,
			name: config.name ?? info.name,
			description: config.description,
			icon: config.icon,
			category: config.category,
			tags: config.tags,
			type: config.type,
			hidden: config.hidden,
		} satisfies BlockDefinition, { keys: blockDefinitionTranslatableKeys });
	}));

	const specialBlocks: Array<BlockBase> = [
		{
			id: slotOutletBlockId,
			name: 'Slot Outlet',
			description: 'Render content from outside the block with slots',
			category: 'special',
			icon: 'lucide:scan-text',
			hidden: true,
		},
	];

	function getBlock(id: string): BlockSerialized | BlockDefinition | undefined {
		return serializedWorkspaceBlocks.value.find((block) => block.id === id)
			?? builtinBlocks.value.find((block) => block.id === id);
	}

	async function resolveBlock(id: string): Promise<BlockSerialized | BlockDefinition> {
		const block = getBlock(id);

		return block ?? useBlockAdapter().loadBlockSerialized(id);
	}

	const refreshHook = createEventHook<string>();

	function onBlockRefresh(callback: (blockId: string) => void): { off: () => void } {
		if (import.meta.server) return { off: () => {} };
		return refreshHook.on(callback);
	}

	function triggerBlockRefresh(blockId: string) {
		if (!import.meta.server) refreshHook.trigger(blockId);
	}

	function useFilteredBlocks(options: UseFilteredBlocksOptions) {
		const filters = reactive(getNewFilters());

		function resetFilters() {
			Object.assign(filters, getNewFilters());
		}

		const filteredBlocks = computed(() => {
			let included: BlockBase[] = [];

			if (filters.include.builtin) {
				included.push(...specialBlocks, ...builtinBlocks.value);
			}

			if (filters.include.workspace) {
				included.push(...serializedWorkspaceBlocks.value.filter((b) => !b.category || !excludedCategories.has(b.category)).toSorted((a, b) => a.name.localeCompare(b.name)));
			}

			const filter = options.filter;

			if (filter) {
				included = included.filter((block) => filter(block));
			}

			if (filters.searchText.trim()) {
				const reg = new RegExp(filters.searchText.trim(), 'i');
				included = included.filter((block) => reg.test(block.name) || reg.test(block.description ?? ''));
			}

			return included;
		});

		const blocksPerCategory = computed(() => {
			const result = new Map<string, BlockBase[]>();

			function add(block: BlockBase) {
				const category = block.category ?? unspecifiedCategory;

				if (!result.has(category)) {
					result.set(category, []);
				}

				result.get(category)!.push(block);
			}

			for (const block of filteredBlocks.value) {
				add(block);
			}

			// Sort blocks
			for (const [category, blocks] of result) {
				result.set(category, blocks.toSorted((a, b) => a.name.localeCompare(b.name)));
			}

			// Sort categories
			const sortedCategories = [...result.keys()].toSorted((a, b) => {
				if (a === unspecifiedCategory) {
					return 1;
				}

				if (b === unspecifiedCategory) {
					return -1;
				}

				return a.localeCompare(b);
			});
			const sortedResult = new Map<string, BlockBase[]>();

			for (const category of sortedCategories) {
				sortedResult.set(category, result.get(category)!);
			}

			return sortedResult;
		});

		return {
			filters,
			resetFilters,
			filteredBlocks,
			blocksPerCategory,
		};
	}

	return {
		getBlock,
		isReady,
		onBlockRefresh,
		resolveBlock,
		triggerBlockRefresh,
		workspaceBlocks: serializedWorkspaceBlocks,
		builtinBlocks,
		useFilteredBlocks,
	};
});

interface Filters {
	searchText: string;
	include: {
		builtin: boolean;
		workspace: boolean;
	};
}

function getNewFilters(): Filters {
	return {
		searchText: '',
		include: {
			builtin: true,
			workspace: true,
		},
	};
}
